import React, { useEffect, useState, FormEvent } from "react";
import {
  Row,
  Col,
  Typography,
  Button,
  Select,
  Input,
  Spin,
  Card,
  Grid,
} from "antd";
import { format } from "date-fns";
import { useFetchApi } from "../../hooks/useFetchApi";
import { nth, CurrencyFormatter, captialCase } from "../../helper";

const { Title } = Typography;
const { useBreakpoint } = Grid;

const DynamicMetricsTab: React.FC<any> = ({
  name,
  stock,
  onSuccess,
}): React.ReactElement => {
  const [quarter, setEditData] = useState<any>({});
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const {
    response: metricData,
    loading: metricLoading,
    execute: getMetricData,
  } = useFetchApi(`/api/metrics-data/${stock.id}`, { name }, "POST");

  const { execute: updateMetrics, loading } = useFetchApi(
    `/api/metrics-data/${stock.id}`,
    {},
    "PUT",
    getMetricData,
  );

  const {
    response: quarters,
    loading: quarterLoading,
    execute: getQuarters,
  } = useFetchApi("/api/quarterByStockId");

  const shareActionOptions = [
    { value: "", label: "Select Corporate Action" },
    { value: "1", label: "Right Issue" },
    { value: "2", label: "Share Split" },
    { value: "3", label: "Consolidation" },
    { value: "4", label: "Script Dividend" },
    { value: "5", label: "Share Split & Right Issue" },
  ];

  const handleChange = (
    e: FormEvent<HTMLInputElement>,
    year: string,
    date: string,
    q: number,
  ) => {
    const { value } = e.currentTarget;

    // const formattedValue = CurrencyFormatter({ amount: value });
    setEditData({
      ...quarter,
      [year]: {
        ...quarter[year],
        [date]: { quarter: q, value },
      },
    });
  };

  const handleDropdownChange = (
    value: string,
    year: string,
    date: string,
    q: number,
  ) => {
    setEditData({
      ...quarter,
      [year]: {
        ...quarter[year],
        [date]: {
          quarter: q,
          value: quarter[year]?.[date]?.value || "",
          shareAction: value,
        },
      },
    });
  };

  const handleAmtAfterRightIssueChange = (
    e: FormEvent<HTMLInputElement>,
    year: string,
    date: string,
    q: number,
  ) => {
    const { value } = e.currentTarget;

    setEditData({
      ...quarter,
      [year]: {
        ...quarter[year],
        [date]: {
          quarter: q,
          value: quarter[year]?.[date]?.value || "",
          shareAction: quarter[year]?.[date]?.shareAction || "",
          amtAfterRightIssue: value,
        },
      },
    });
  };

  const isSharesMetric = name === "total_number_of_issued_shares";

  const handleUpdate = () => {
    console.log("{ quarter, stock_id: selectedStock }", {
      quarter,
      stock_id: stock.id,
    });
    updateMetrics({ name, quarter, stock_id: stock.id });
  };

  useEffect(() => {
    getMetricData();
    getQuarters({
      companyQuarterId: stock.company_quarter_id,
      company_start_date: stock.company_start_date,
    });
  }, [name]);

  useEffect(() => {
    if (metricData && Object.keys(metricData).length) {
      const mData: any = {};
      Object.keys(metricData).forEach((year: any) => {
        metricData[year].forEach((m: any) => {
          mData[year] = {
            ...mData[year],
            [m.date]: {
              quarter: m.quarter,
              value: m.amount + "",
              shareAction: isSharesMetric
                ? m.corporate_action || ""
                : undefined,
              amtAfterRightIssue: isSharesMetric
                ? m.amount_after_right_issue || ""
                : undefined,
            },
          };
        });
      });

      setEditData(mData);
    } else {
      setEditData([]);
    }
  }, [metricData, stock, name]);

  return (
    <Spin spinning={metricLoading || quarterLoading || loading}>
      <div style={{ width: "100%" }}>
        <Row
          justify="space-between"
          align="middle"
          gutter={[8, 8]}
          style={{ paddingBottom: 8 }}
        >
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              {captialCase(name)}
            </Title>
          </Col>
          <Col>
            <Button
              type="primary"
              size={isMobile ? "middle" : "middle"}
              onClick={handleUpdate}
              block={isMobile}
            >
              Update
            </Button>
          </Col>
        </Row>

        {quarters && Object.keys(quarters)
          ? Object.keys(quarters)
              .sort((a: any, b: any) => b - a)
              .map((year: any) => (
                <Card
                  key={year}
                  title={year}
                  size="small"
                  style={{ marginTop: 16 }}
                  bodyStyle={{ overflowX: "auto", padding: 0 }}
                >
                  <div style={{ display: "flex", minWidth: "max-content" }}>
                    {quarters[year].map((metric: any, key: number) => {
                      const currentQuarter = quarter?.[year]?.[metric.date];
                      const showAdditionalInput =
                        isSharesMetric && currentQuarter?.shareAction == "5";

                      return (
                        <div
                          key={metric.date}
                          style={{
                            width: 220,
                            minWidth: 220,
                            borderRight: "1px dashed #d9d9d9",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <div
                            style={{
                              background: "#001529",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              textAlign: "center",
                              height: 48,
                              padding: "0 8px",
                              fontSize: 12,
                            }}
                          >
                            {format(new Date(metric.date), "do MMMM yyyy")} |{" "}
                            {`${key + 1}${nth(key + 1)} Qtr`}
                          </div>

                          {isSharesMetric && (
                            <div
                              style={{
                                padding: 8,
                                borderBottom: "1px dashed #d9d9d9",
                              }}
                            >
                              <Select
                                style={{ width: "100%" }}
                                value={
                                  quarter &&
                                  quarter[year] &&
                                  quarter[year][metric.date] &&
                                  quarter[year][metric.date].shareAction
                                    ? quarter[year][metric.date].shareAction
                                    : ""
                                }
                                onChange={(value: string) =>
                                  handleDropdownChange(
                                    value,
                                    year,
                                    metric.date,
                                    key + 1,
                                  )
                                }
                                options={shareActionOptions}
                              />
                            </div>
                          )}

                          <div
                            style={{
                              padding: 8,
                              borderBottom: "1px dashed #d9d9d9",
                            }}
                          >
                            <Input
                              id={metric.date}
                              placeholder="amount here..."
                              onChange={(e: any) =>
                                handleChange(e, year, metric.date, key + 1)
                              }
                              value={
                                quarter &&
                                quarter[year] &&
                                quarter[year][metric.date] &&
                                quarter[year][metric.date].value
                                  ? quarter[year][metric.date].value
                                  : ""
                              }
                            />
                          </div>

                          {showAdditionalInput && (
                            <div style={{ padding: 8 }}>
                              <Input
                                id={`additional-${metric.date}`}
                                placeholder="amount after right issue..."
                                onChange={(e: any) =>
                                  handleAmtAfterRightIssueChange(
                                    e,
                                    year,
                                    metric.date,
                                    key + 1,
                                  )
                                }
                                value={
                                  quarter &&
                                  quarter[year] &&
                                  quarter[year][metric.date] &&
                                  quarter[year][metric.date].amtAfterRightIssue
                                    ? quarter[year][metric.date]
                                        .amtAfterRightIssue
                                    : ""
                                }
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              ))
          : null}
      </div>
    </Spin>
  );
};

export default DynamicMetricsTab;
